import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Input } from './Input'
import { Pill, type Tone } from './Pill'
import { Chip } from './Chip'
import { SegmentedControl } from './SegmentedControl'
import { Form, type FormField } from './Form'
import { FilterBuilder, type FilterFieldDef, type Filter } from './FilterBuilder'
import { Modal } from './Modal'
import { cx } from './cx'
import { IconTile } from './IconTile'
import s from './Lookup.module.css'
import { useOverlayEscapeLayer, useOverlayPortalTarget } from './OverlayPortal'

/**
 * Lookup — the ONE configurable record/entity lookup. Company lookup, the D365
 * relation picker, add-many-children, any "search a source → pick/collect (→ create
 * it)" surface is a configuration of this, not a bespoke dialog. It owns query +
 * debounce + async race-guard + facet/type state + keyboard nav + create-in-flow +
 * single/multi selection + list/card layout + the two presentations (anchored
 * `popover`, modal `dialog`). Callers supply data via `search(query, facet, type)`
 * and how a hit renders via LookupItem.
 */
export interface LookupItem {
  id: string
  primary: ReactNode
  secondary?: ReactNode
  glyph?: ReactNode
  badge?: { label: string; tone?: Tone }
}
export interface LookupSection {
  title?: string
  items: LookupItem[]
  /** False means this bounded answer must not be presented as the whole set. */
  complete?: boolean
}
export interface LookupFacet { key: string; label: string; count?: number }
export interface LookupType { key: string; label: string }

export interface LookupProps {
  presentation?: 'popover' | 'dialog'
  /** popover: the element to anchor + clamp against */
  anchor?: HTMLElement | null
  onClose: () => void
  /**
   * single mode: called on pick.
   *
   * Return NOTHING and the surface closes on the click, exactly as before.
   * Return a PROMISE and it stays open until that promise settles: it closes
   * on resolve, and on rejection it stays open with the refusal reachable and
   * the person still able to choose again (#2149).
   */
  onPick?: (item: LookupItem, selectedType: string | null) => void | Promise<unknown>
  /** multi mode: called on commit with the whole basket. Same contract as `onPick`. */
  onCommit?: (items: LookupItem[]) => void | Promise<unknown>
  mode?: 'single' | 'multi'
  layout?: 'list' | 'cards'
  renderItem?: (item: LookupItem) => ReactNode
  /** the data source — sync (relation picker) or async (registry search) */
  search: (query: string, facet: string | null, type: string | null, filters: Filter[]) => LookupSection[] | Promise<LookupSection[]>
  /** advanced search — a multi-field filter builder (emits the grid/MCP filter payload) */
  advanced?: { fields: FilterFieldDef[] }
  /** graph proximity — a section shown at the top on zero query (from get_neighbours) */
  suggest?: () => LookupSection | Promise<LookupSection>
  /** MDM nudge — likely-existing matches for a to-be-created query (shown in create) */
  dedupe?: (query: string) => LookupItem[]
  /** polymorphic endpoints — a type switch appears when more than one */
  types?: LookupType[]
  defaultType?: string
  onTypeChange?: (type: string) => void
  title?: string
  hint?: ReactNode
  placeholder?: string
  width?: number
  minChars?: number
  debounceMs?: number
  facets?: (query: string) => LookupFacet[]
  create?: { show: (query: string) => boolean; label: (query: string) => ReactNode; onCreate: (query: string) => void | Promise<void> }
  /** quick-create beside the lookup: choosing "Create" opens a Form; submit returns the new item, which is then picked/added */
  quickCreate?: {
    title?: string
    fields: FormField[]
    initial?: (query: string) => Record<string, unknown>
    submit: (values: Record<string, unknown>) => LookupItem | Promise<LookupItem>
    submitLabel?: string
  }
  commitLabel?: (n: number) => string
  footer?: ReactNode
  emptyText?: ReactNode
  /** testid on the root panel */
  testid?: string
}

export function Lookup({
  presentation = 'popover', anchor, onClose, onPick, onCommit, mode = 'single', layout = 'list', renderItem, search,
  advanced, suggest, dedupe, types, defaultType, onTypeChange, title, hint, placeholder = 'Search…',
  width = presentation === 'dialog' ? 560 : 360,
  minChars = 0, debounceMs = 0, facets, create, quickCreate, commitLabel = (n) => `Add ${n}`, footer, emptyText = 'Nothing matches.', testid,
}: LookupProps) {
  const [q, setQ] = useState('')
  const [facet, setFacet] = useState<string | null>(null)
  const [type, setType] = useState<string | null>(
    defaultType ?? (types?.length === 1 ? types[0]!.key : null))
  const [advOpen, setAdvOpen] = useState(false)
  const [filters, setFilters] = useState<Filter[]>([])
  const [sections, setSections] = useState<LookupSection[]>([])
  const [busy, setBusy] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [retryRevision, setRetryRevision] = useState(0)
  const [active, setActive] = useState(0)
  const [sel, setSel] = useState<Map<string, LookupItem>>(new Map()) // the basket (multi)
  const [creating, setCreating] = useState(false)
  const [createDraft, setCreateDraft] = useState<Record<string, unknown>>({})
  const [createQuery, setCreateQuery] = useState('')
  const [suggestSection, setSuggestSection] = useState<LookupSection | null>(null)
  const seq = useRef(0)

  // proximity suggestions (graph) — loaded once, shown at the top on zero query
  useEffect(() => {
    if (!suggest) return
    const out = suggest()
    if (out instanceof Promise) void out.then(setSuggestSection); else setSuggestSection(out)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- suggest identity is caller-owned
  }, [])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // run the source (debounced, race-guarded) whenever query/facet/type changes
  useEffect(() => {
    // Invalidate the prior answer NOW, not after this question's debounce. A
    // late refusal from the previous query must never be rendered as the answer
    // to the text currently in the box.
    const effectSeq = ++seq.current
    if (q.trim().length < minChars) {
      setSections([])
      setBusy(false)
      setSearchError(null)
      return
    }
    const go = () => {
      const mySeq = effectSeq
      setSearchError(null)
      setBusy(true)
      const failed = (error: unknown) => {
        if (mySeq !== seq.current) return
        const message = (error as { message?: unknown } | null)?.message
        setSections([])
        setBusy(false)
        setSearchError(typeof message === 'string' && message.trim()
          ? message
          : 'This search is unavailable. Try again.')
      }
      try {
        const out = search(q, facet, type, filters)
        if (out instanceof Promise) {
          void out.then(
            (res) => {
              if (mySeq === seq.current) {
                setSections(res)
                setBusy(false)
              }
            },
            failed,
          )
        } else {
          setSections(out)
          setBusy(false)
        }
      } catch (error) {
        failed(error)
      }
    }
    if (debounceMs > 0) {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(go, debounceMs)
      return () => { if (timer.current) clearTimeout(timer.current) }
    }
    go()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- search identity is caller-owned
  }, [q, facet, type, filters, minChars, debounceMs, retryRevision])

  const shownSections = useMemo(
    () => (!q.trim() && suggestSection && suggestSection.items.length ? [suggestSection, ...sections] : sections),
    [q, suggestSection, sections],
  )
  const flat = useMemo(() => shownSections.flatMap((sec) => sec.items), [shownSections])
  const showCreate = !!create && create.show(q)
  const total = flat.length + (showCreate ? 1 : 0)
  // A new search is a new question; the last refusal is no longer the answer.
  /* A new search is a new question; the last refusal is no longer the answer.
     `filters` belongs here with the rest of the query shape: the advanced
     builder changes WHAT IS BEING ASKED just as much as the text box does, and
     leaving it out kept a refusal from one attempt visible over the results of
     a different one (audit point 4). */
  useEffect(() => { setActive(0); setRefused(null) }, [q, facet, type, filters])

  /**
   * A governed selection is not finished when the click is (#2149).
   *
   * This control used to run `onPick?.(…)` and `onClose()` back to back, so a
   * caller that started a write inside `onPick` had its surface torn down on
   * the click — a refusal then had nothing left on screen to fail on, and a
   * success closed over a list that had not been refetched. Awaiting inside
   * the handler could not help: the close never came from the handler.
   *
   * The shape is borrowed from the CREATE path, which has always left the
   * ordering to its caller (`create.onCreate` below is invoked without a close
   * beside it) and whose one caller therefore gets it right. This gives the
   * PICK path the same property, without asking every caller to re-derive it.
   *
   * `pendingRef` exists beside the state because the dismissal routes that
   * matter are document-level listeners and Radix callbacks: they read this
   * during an event, not during a render, and a captured `pending` would be
   * whatever it was when the listener was registered.
   */
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(false)
  /* What the server said, kept IN the surface. Staying open on a refusal is
     only half the repair: the caller's own toast may sit behind a modal, and a
     dialog that reopens itself saying nothing reads as a control that ate the
     click. Retry is picking again — there is no separate button, because the
     list is right there and a retry of a governed write is the same write. */
  const [refused, setRefused] = useState<string | null>(null)
  const setPendingBoth = (v: boolean) => { pendingRef.current = v; setPending(v) }

  const isThenable = (v: unknown): v is Promise<unknown> =>
    typeof (v as { then?: unknown } | null)?.then === 'function'

  /* A thrown thing is not always an Error, and an empty message is worse than
     a plain sentence — "" would render an alert that says nothing at all. */
  const refusalText = (e: unknown): string => {
    const m = (e as { message?: unknown } | null)?.message
    return typeof m === 'string' && m.trim() ? m : 'That could not be saved.'
  }

  /** Close now, or when the caller's write settles — and never on refusal. */
  const closeWhenSettled = (result: void | Promise<unknown>) => {
    if (!isThenable(result)) { onClose(); return }
    setPendingBoth(true)
    result.then(
      () => { setPendingBoth(false); onClose() },
      /* Stays OPEN, and says why. The caller's own channel still speaks — a
         local onError, or the mutation-cache net — but the person keeps the
         surface they were working in, with the basket and the query intact,
         and the reason where they are looking. */
      (err: unknown) => { setPendingBoth(false); setRefused(refusalText(err)) },
    )
  }

  /**
   * Every OTHER way out of this control, funnelled through one rule.
   *
   * Escape, the close button, an outside pointer and Radix's own dismissal are
   * four separate routes; refusing them one at a time is how three of them stay
   * open. While a governed write is outstanding they are all refused, because
   * dismissing the surface would not cancel the write — it would only hide
   * whether it worked.
   */
  const requestClose = useCallback(() => { if (!pendingRef.current) onClose() }, [onClose])

  const toggle = (it: LookupItem) => setSel((prev) => { const m = new Map(prev); if (m.has(it.id)) m.delete(it.id); else m.set(it.id, it); return m })
  const pick = (it: LookupItem) => {
    // A second pick while the first is still being written would start a
    // second governed write from a surface already committed to the first.
    if (pendingRef.current) return
    setRefused(null)
    if (mode === 'multi') toggle(it)
    else closeWhenSettled(onPick?.(it, type))
  }
  const openCreate = () => { setCreateQuery(q.trim()); setCreateDraft(quickCreate!.initial?.(q.trim()) ?? {}); setCreating(true) }
  const doCreate = () => { if (quickCreate) openCreate(); else if (create) void create.onCreate(q.trim()) }
  const doQuickCreate = async () => {
    if (!quickCreate) return
    const it = await quickCreate.submit(createDraft)
    setCreating(false)
    if (mode === 'multi') toggle(it); else closeWhenSettled(onPick?.(it, type))
  }
  const commit = () => {
    if (pendingRef.current) return
    closeWhenSettled(onCommit?.([...sel.values()]))
  }

  const onKey = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Escape') { e.preventDefault(); requestClose() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % Math.max(1, total)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + total) % Math.max(1, total)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      if (mode === 'multi' && (e.metaKey || e.ctrlKey)) return commit()
      if (active < flat.length && flat[active]) pick(flat[active]!)
      else if (showCreate) doCreate()
    } else if (e.key === 'Backspace' && !q && facet) setFacet(null)
  }

  const facetList = facets ? facets(q) : []
  let idx = -1
  const hit = (it: LookupItem) => {
    idx += 1; const i = idx
    const picked = mode === 'multi' && sel.has(it.id)
    const common = { role: 'option' as const, 'data-active': i === active || undefined, onClick: () => pick(it), onMouseMove: () => setActive(i) }
    if (renderItem) return <button key={it.id} type="button" className={cx(layout === 'cards' ? s.card : s.item, picked && s.picked)} {...common}>{mode === 'multi' && <span className={s.check}>{picked ? '✓' : ''}</span>}{renderItem(it)}</button>
    if (layout === 'cards') return (
      <button key={it.id} type="button" className={cx(s.card, picked && s.picked)} {...common}>
        <span className={s.cardTop}>
          {mode === 'multi' && <span className={s.check}>{picked ? '✓' : ''}</span>}
          {it.glyph != null && <IconTile>{it.glyph}</IconTile>}
          {it.badge && <span className={s.cardBadge}><Pill tone={it.badge.tone ?? 'neutral'}>{it.badge.label}</Pill></span>}
        </span>
        <span className={s.primary}>{it.primary}</span>
        {it.secondary != null && <span className={s.secondary}>{it.secondary}</span>}
      </button>
    )
    return (
      <button key={it.id} type="button" className={cx(s.item, picked && s.picked)} {...common}>
        {mode === 'multi' && <span className={s.check}>{picked ? '✓' : ''}</span>}
        {it.glyph != null && <IconTile>{it.glyph}</IconTile>}
        <span className={s.main}><span className={s.primary}>{it.primary}</span>{it.secondary != null && <span className={s.secondary}>{it.secondary}</span>}</span>
        {it.badge && <Pill tone={it.badge.tone ?? 'neutral'}>{it.badge.label}</Pill>}
      </button>
    )
  }

  const createInner = quickCreate ? (
    <>
      <div className={s.head}>
        <span className={s.headT}>{quickCreate.title ?? 'Create'}</span>
        <span className={s.headSp} />
        <button type="button" aria-label="Back to results" className={s.close} onClick={() => setCreating(false)}>✕</button>
      </div>
      <div className={s.createBody}>
        {(() => {
          const dupes = dedupe ? dedupe(createQuery) : []
          return dupes.length ? (
            <div className={s.dedupe} data-testid={testid ? `${testid}-dedupe` : undefined}>
              <div className={s.dedupeHd}>⚠ Looks like this already exists — pick one instead of creating a duplicate:</div>
              {dupes.map((d) => (
                <button key={d.id} type="button" className={s.dedupeRow} onClick={() => pick(d)}>
                  {d.glyph != null && <IconTile>{d.glyph}</IconTile>}
                  <span className={s.primary}>{d.primary}</span>
                  {d.secondary != null && <span className={s.secondary}>{d.secondary}</span>}
                </button>
              ))}
            </div>
          ) : null
        })()}
        <Form fields={quickCreate.fields} value={createDraft} onChange={(p) => setCreateDraft((d) => ({ ...d, ...p }))}
          onSubmit={doQuickCreate} onCancel={() => setCreating(false)} submitLabel={quickCreate.submitLabel ?? 'Create'} variant="quick" />
      </div>
    </>
  ) : null

  const mainBody = (
    <>
      <div className={s.searchRow}>
        <Input autoFocus placeholder={placeholder} value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} data-testid={testid ? `${testid}-q` : undefined} />
        {advanced && (
          <button type="button" className={cx(s.advBtn, advOpen && s.advOn)} onClick={() => setAdvOpen((v) => !v)}
            data-testid={testid ? `${testid}-adv` : undefined}>
            ▸ Advanced{filters.length > 0 && <span className={s.advN}>{filters.length}</span>}
          </button>
        )}
      </div>
      {advanced && advOpen && (
        <div className={s.advRow}>
          <FilterBuilder fields={advanced.fields} value={filters} onChange={setFilters} testid={testid ? `${testid}-filters` : undefined} />
        </div>
      )}
      {types && types.length > 1 && (
        <div className={s.typeRow} data-testid={testid ? `${testid}-types` : undefined}>
          <SegmentedControl size="sm" label="Filter by type" value={type}
            onChange={(next) => { setType(next); onTypeChange?.(next) }}
            options={types.map((t) => ({ value: t.key, label: t.label }))} />
        </div>
      )}
      {facetList.length > 0 && (
        <div className={s.facets}>
          {facetList.map((f) => (
            <Chip key={f.key} active={facet === f.key} onClick={() => setFacet(facet === f.key ? null : f.key)}>
              {f.label}{f.count != null && <span className={s.facetN}>{f.count}</span>}
            </Chip>
          ))}
        </div>
      )}
      <div className={s.list} role="listbox" aria-label={title ?? placeholder}
        aria-busy={pending || undefined} data-testid={testid ? `${testid}-hits` : undefined}>
        {shownSections.map((sec, si) => (
          <div key={sec.title ?? si}>
            {sec.title && <div className={s.sect}>{sec.title}</div>}
            {layout === 'cards' ? <div className={s.cards}>{sec.items.map(hit)}</div> : sec.items.map(hit)}
            {sec.complete === false && (
              <div className={s.sect} data-testid={testid ? `${testid}-incomplete` : undefined}>
                {`First ${sec.items.length} matches shown · more may exist`}
              </div>
            )}
          </div>
        ))}
        {showCreate && create && (
          <button type="button" className={cx(s.item, s.createIt)} data-active={active === flat.length || undefined}
            onClick={doCreate} onMouseMove={() => setActive(flat.length)}>
            <IconTile>＋</IconTile> {create.label(q.trim())}
          </button>
        )}
        {busy && <div className={s.status}>Searching…</div>}
        {searchError && !busy && (
          <div className={s.status} role="alert"
            data-testid={testid ? `${testid}-search-error` : undefined}>
            <span>{searchError}</span>{' '}
            <button type="button" className={s.retry}
              data-testid={testid ? `${testid}-retry` : undefined}
              onClick={() => setRetryRevision((revision) => revision + 1)}>Try again</button>
          </div>
        )}
        {/* Announced, not merely drawn (#2149). A person who cannot see the
            dialog otherwise finds it inert: the keys stop working and nothing
            says why. `role="status"` is polite on purpose — it must not
            interrupt the refusal the caller may be about to speak. */}
        {refused && (
          <div className={s.status} role="alert"
            data-testid={testid ? `${testid}-refused` : undefined}>{refused}</div>
        )}
        {pending && (
          <div className={s.status} role="status" aria-live="polite"
            data-testid={testid ? `${testid}-pending` : undefined}>Saving your choice…</div>
        )}
        {!busy && !searchError && total === 0 && <div className={s.status}>{emptyText}</div>}
      </div>
      {mode === 'multi' && (
        <div className={s.commitBar} data-testid={testid ? `${testid}-commit` : undefined}>
          <span className={cx(s.commitN, 'tnum')} data-testid={testid ? `${testid}-count` : undefined}>{sel.size}</span>
          <span>selected — the basket survives your next search</span>
          <span className={s.grow} />
          <button type="button" className={s.commitBtn} disabled={sel.size === 0 || pending} onClick={commit}>{commitLabel(sel.size)} ▸</button>
        </div>
      )}
      {footer && <div className={s.footer}>{footer}</div>}
    </>
  )

  if (presentation === 'dialog') return (
    <DialogShell onClose={requestClose} dismissable={!pending} width={width} title={title} hint={hint} testid={testid}
      aside={creating ? <div className={s.createPanel} data-testid={testid ? `${testid}-create` : undefined}>{createInner}</div> : undefined}>
      {mainBody}
    </DialogShell>
  )
  return (
    <PopoverShell anchor={anchor ?? null} onClose={requestClose} width={width} testid={testid}>
      {creating ? <div data-testid={testid ? `${testid}-create` : undefined}>{createInner}</div> : mainBody}
    </PopoverShell>
  )
}

/* anchored, viewport-clamped, closes on outside/Esc */
function PopoverShell({ anchor, onClose, width, testid, children }: { anchor: HTMLElement | null; onClose: () => void; width: number; testid?: string; children: ReactNode }) {
  useOverlayEscapeLayer()
  const overlayPortalTarget = useOverlayPortalTarget()
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current; if (!el || !anchor) return
    const r = anchor.getBoundingClientRect()
    el.style.left = Math.min(Math.max(8, r.left), innerWidth - el.offsetWidth - 8) + 'px'
    const top = r.bottom + 5 + el.offsetHeight > innerHeight - 8 ? Math.max(8, innerHeight - el.offsetHeight - 8) : r.bottom + 5
    el.style.top = top + 'px'
  })
  useEffect(() => {
    const down = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node) && !anchor?.contains(e.target as Node)) onClose() }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key, true)
    return () => { document.removeEventListener('pointerdown', down); document.removeEventListener('keydown', key, true) }
  }, [anchor, onClose])
  // DEMOTED from role="dialog" (#387/UX-02). This is an anchored, non-modal
  // popover: nothing is trapped, the page behind stays live, and it carried no
  // accessible name — so a screen reader announced the single word "dialog" and
  // then promised modal semantics that do not exist. The children already carry
  // the real semantics (a textbox and a role="listbox"), which is what a combobox
  // popup should expose. Trapping it instead would have made it worse.
  // `maxWidth` rather than `width` alone (#967). The popover was a fixed 360px
  // and only its LEFT edge was clamped, so in any viewport narrower than that —
  // a Chrome side panel, which a user drags to whatever width they like, and a
  // phone in portrait — the panel ran off the right-hand side and the clamp
  // above pushed it off the LEFT instead. This is a subtraction from the shared
  // component, not a variant of it: nothing renders differently anywhere the
  // viewport was already wide enough, and no caller opts in.
  return createPortal(
    <div ref={ref} className={s.pop} style={{ width, maxWidth: 'calc(100vw - 16px)' }} data-testid={testid}>{children}</div>,
    overlayPortalTarget ?? document.body)
}

/* centered modal overlay, closes on backdrop/Esc; `aside` opens a split panel (quick-create) */
function DialogShell({ onClose, dismissable = true, width, title, hint, testid, aside, children }: { onClose: () => void; dismissable?: boolean; width: number; title?: string; hint?: ReactNode; testid?: string; aside?: ReactNode; children: ReactNode }) {
  // #387/UX-02. This was operable — the search input carries `autoFocus` and the
  // list is arrow/Enter driven — so the migration is a correctness upgrade, not a
  // rescue: it adds the focus TRAP, `aria-modal`, focus restoration and the scroll
  // lock, and replaces the hand-rolled capture-phase Escape listener (which closed
  // every stacked Lookup at once) with Modal's layer stack.
  //
  // The name is now REQUIRED: `title` is optional on LookupProps, so an untitled
  // dialog lookup used to ship `aria-label={undefined}` and be announced as the
  // single word "dialog". `'Search'` is the honest fallback for what it is.
  return (
    /* `dismissable` is threaded rather than left at its default because Radix
       owns two of this dialog's exits (Escape and outside-pointer) and the
       caller can reach neither. Modal reads it through a live ref, so flipping
       it while the dialog is open is honoured — measured under #1466 A. */
    <Modal label={title || 'Search'} onClose={onClose} dismissable={dismissable} width={width} testid={testid}
      overlayClassName={s.overlay} wrapClassName={s.splitWrap} className={s.dialog} aside={aside}>
      {(title || hint) && (
        <div className={s.head}>
          {title && <span className={s.headT}>{title}</span>}
          {hint && <span className={s.headHint}>{hint}</span>}
          <span className={s.headSp} />
          <button type="button" aria-label="Close" className={s.close} onClick={onClose}>✕</button>
        </div>
      )}
      {children}
    </Modal>
  )
}
